import React, { PureComponent } from 'react';
import PropTypes from 'prop-types';
import isEmpty from 'lodash/isEmpty';
import isNumber from 'lodash/isNumber';
import classnames from 'classnames';

export default class FormControl extends PureComponent {
  static propTypes = {
    children: PropTypes.element.isRequired,
    name: PropTypes.string.isRequired,
    label: PropTypes.string.isRequired,

    onChange: PropTypes.func,
    value: PropTypes.oneOfType([PropTypes.string, PropTypes.number, PropTypes.array]),
    required: PropTypes.bool,
    showErrorByForce: PropTypes.bool,
    suffix: PropTypes.string,
    onBlur: PropTypes.func,
    validate: PropTypes.func,
    shouldReset: PropTypes.bool,
    resetValue: PropTypes.func,
    shouldHide: PropTypes.bool,
    validationSubscription: PropTypes.func,
    customErrorMessage: PropTypes.string,
    horizontal: PropTypes.bool,
    hasSideComponent: PropTypes.bool,
    sideComponent: PropTypes.node,
    customValidation: PropTypes.func,
    hideLabel: PropTypes.bool,
    globalFormError: PropTypes.node,
    containerClassName: PropTypes.string,
    className: PropTypes.string,
    hideAsterisk: PropTypes.bool,
    forceAsterisk: PropTypes.bool,
  };

  static defaultProps = {
    horizontal: false,
    hasSideComponent: false,
    sideComponent: null,
    customValidation: null,
    globalFormError: '',
    containerClassName: '',
    className: '',
    hideAsterisk: false,
    hideLabel: false,
    forceAsterisk: false,
  };

  state = {
    dirty: false,
    errorMessage: null,
  };

  componentDidMount() {
    if (this.props.validationSubscription && !this.props.shouldHide) {
      this.props.validationSubscription(this.props.name, 'subscribe');
    }
  }

  componentDidUpdate(prevProps) {
    this.validateOnComponentDidUpdate(prevProps);
    if (
      this.props.validationSubscription
      && (prevProps.shouldHide !== this.props.shouldHide)
      && !this.props.shouldHide
    ) {
      this.props.validationSubscription(this.props.name, 'subscribe');
    }

    if (
      this.props.validationSubscription
      && (prevProps.shouldHide !== this.props.shouldHide)
      && this.props.shouldHide
    ) {
      this.props.validationSubscription(this.props.name, 'unsubscribe');
    }

    if (
      ((prevProps.shouldReset !== this.props.shouldReset) && this.props.shouldReset)
      || ((prevProps.shouldHide !== this.props.shouldHide) && this.props.shouldHide)
    ) {
      if (this.props.resetValue) {
        this.props.resetValue(this.props.name);
      }

      this.setState({
        dirty: false,
        errorMessage: null,
      });
    }
  }

  validateOnComponentDidUpdate = prevProps => {
    const valueChanged = prevProps.value !== this.props.value;
    const showErrorByForceBecameActive = !prevProps.showErrorByForce && this.props.showErrorByForce;
    const becameVisible = (this.props.shouldHide !== prevProps.shouldHide) && !this.props.shouldHide;
    const requiredChanged = prevProps.required !== this.props.required;

    if ((valueChanged && !this.props.shouldHide)
      || (showErrorByForceBecameActive && !this.props.shouldHide)
      || (becameVisible && this.props.showErrorByForce)
      || (requiredChanged && !this.props.shouldHide)
    ) {
      this.setState({ dirty: true }, () => this.validate());
    }

    if (prevProps.customErrorMessage !== this.props.customErrorMessage) {
      this.setState({ errorMessage: this.props.customErrorMessage });
    }
  };

  onFocus = () => this.setState({ dirty: true });

  onBlur = event => {
    if (event && event.persist) {
      event.persist();
    }

    if (this.props.onBlur) {
      this.props.onBlur(event);
    }
  };

  onChange = event => {
    if (event && event.persist) {
      event.persist();
    }

    if (this.props.onChange) {
      this.props.onChange(event);
    }
  };

  validate = () => {
    const {
      customErrorMessage,
      label,
      required,
      name,
      shouldHide,
      value,
      validate,
      customValidation,
    } = this.props;
    const { dirty } = this.state;

    if (!dirty || shouldHide || !validate) {
      return;
    }

    if (
      required && (
        (isNumber(value) && value === 0)
        || (!isNumber(value) && isEmpty(value))
      )
    ) {
      const errorMessage = customErrorMessage || `${label} is required`;
      this.setState({ errorMessage }, () => validate(name, { isValid: false }));
      return;
    }

    if (customValidation) {
      const errorMessage = customValidation(value);

      if (errorMessage) {
        this.setState({ errorMessage }, () => validate(name, { isValid: false }));
        return;
      }

      this.setState({ errorMessage: null }, () => validate(name, { isValid: true }));
    }

    this.setState({ errorMessage: null }, () => validate(name, { isValid: true }));
  };

  forceValidate = () => {
    this.validate();
  };

  render() {
    const {
      name,
      label,
      value,
      required,
      suffix,
      children,
      horizontal,
      hasSideComponent,
      sideComponent,
      hideLabel,
      globalFormError,
      containerClassName,
      hideAsterisk,
      forceAsterisk,
      className: formGroupClassNameProp,
    } = this.props;
    const { errorMessage, dirty } = this.state;

    const suffixElement = suffix ? <span className="suffix">{suffix}</span> : null;

    const isInput = (children.type === 'input' && children.props.type !== 'checkbox')
      || children.type === 'textarea';

    const classNames = classnames(
      {
        'form-control': !isInput,
        'a-input-text': isInput,
      },
      children.props.className
    );
    const formGroupClassName = classnames(formGroupClassNameProp, { 'row align-items-center': horizontal, 'a-input-sm': isInput, 'form-group': !isInput });
    const labelClassName = classnames({ 'col-3 col-form-label': horizontal });
    const childrenClassName = classnames(
      {
        'form-control-container': !isInput,
        'a-input-compound': isInput,
        'col-4': horizontal && hasSideComponent,
        'col-9': horizontal && !hasSideComponent,
      },
      containerClassName
    );
    const errorClassName = classnames('invalid-feedback', {
      'col-9': horizontal,
      'offset-3': horizontal && !!label,
    });

    return this.props.shouldHide ? null : (
      <div className={formGroupClassName}>
        {label && !hideLabel
          ? (
            <label className={labelClassName} htmlFor={name}>
              {label}
              {((required && !hideAsterisk) || forceAsterisk) && <span className="form-asterisk">&nbsp;*</span>}
            </label>
          )
          : ((required && !hideAsterisk) || forceAsterisk) && <span className="form-asterisk no-label">*</span>
        }
        <div className={childrenClassName}>
          {React.cloneElement(children, {
            className: classNames,
            id: name,
            onChange: this.onChange,
            onFocus: this.onFocus,
            onBlur: this.onBlur,
            name,
            value,
          })}
          {suffixElement}
        </div>
        {sideComponent
          && (
            <div className={classnames({ 'col-5': horizontal })}>
              {sideComponent}
            </div>
          )}
        {(globalFormError || (dirty && errorMessage))
          ? (
            <div className={errorClassName}>
              {globalFormError || errorMessage}
            </div>
          )
          : null}
      </div>
    );
  }
}
